/**
 * Size Finder - v2.0.1 (API-powered, chart-corrected)
 *
 * v2.0.1: Static fallback overrides API size_code for known models
 * v2.0.0: Replaces hardcoded vehicle array with live vehicle-fitment API
 *
 * Uses same localStorage contract as PDP (hp_selected_vehicle) so
 * selecting here auto-restores on PDP load.
 *
 * API: vehicle-fitment v6 (Supabase edge function)
 * Endpoints: ?action=years|makes|models|lookup
 */
document.addEventListener('DOMContentLoaded', function() {
  'use strict';

  // ========================================
  // CONFIG
  // ========================================
  var API_BASE = 'https://vrpmnxvvmmmwhkbvypyh.supabase.co/functions/v1/vehicle-fitment';
  var STORAGE_KEY = 'hp_selected_vehicle';
  var PDP_URL = '/products/hailprotector-vehicle-protection-system';
  // Legacy anon JWT — required since the edge fn was hardened to verify_jwt:true.
  // This is the PUBLIC anon key (safe to expose in frontend; only satisfies the gateway).
  var API_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZycG1ueHZ2bW1td2hrYnZ5cHloIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE4ODI1ODAsImV4cCI6MjA4NzQ1ODU4MH0.ysPKqfdCfbh0LGamXaqruzonvwooliYfG97c_S0Egws';

  // Size metadata (same as PDP)
  // Chart-spec boundaries (updated 2026-03-04)
  var SIZE_META = {
    'CAR1': { series: 'Sentinel', tier: 'Compact', range: 'Up to 175"',   price: '$549', system: 'sentinel' },
    'CAR2': { series: 'Sentinel', tier: 'Mid',     range: '176" – 195"',  price: '$549', system: 'sentinel' },
    'CAR3': { series: 'Sentinel', tier: 'Full',    range: '196" – 211"',  price: '$549', system: 'sentinel' },
    'SUV1': { series: 'Guardian', tier: 'Compact', range: 'Up to 197"',   price: '$649', system: 'guardian' },
    'SUV2': { series: 'Guardian', tier: 'Mid',     range: '198" – 233"',  price: '$649', system: 'guardian' },
    'SUV3': { series: 'Guardian', tier: 'Full',    range: 'Up to 270"',   price: '$649', system: 'guardian' }
  };

  // ========================================
  // DOM REFS
  // ========================================
  var tabs       = document.querySelectorAll('.sf-tab');
  var panels     = document.querySelectorAll('.sf-panel');
  var yearSelect  = document.getElementById('sf-year');
  var makeSelect  = document.getElementById('sf-make');
  var modelSelect = document.getElementById('sf-model');
  var ymmBtn      = document.getElementById('sf-ymm-btn');
  var popularTags = document.querySelectorAll('.sf-tag');
  var lengthInput = document.getElementById('length-input');
  var typeBtns    = document.querySelectorAll('.sf-type-btn');
  var findByLengthBtn = document.getElementById('find-by-length');
  var resultEl    = document.getElementById('size-result');
  var resetBtn    = document.getElementById('reset-finder');

  // ========================================
  // API HELPERS
  // ========================================
  function apiFetch(params) {
    var url = API_BASE + '?v=6';
    Object.keys(params).forEach(function(k) {
      url += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
    });
    return fetch(url, {
      headers: { 'Authorization': 'Bearer ' + API_KEY }
    }).then(function(r) { return r.json(); });
  }

  function resetSelect(sel, placeholder) {
    sel.innerHTML = '<option value="">' + placeholder + '</option>';
    sel.disabled = true;
  }

  function populateSelect(sel, items, placeholder, valueFn, labelFn) {
    sel.innerHTML = '<option value="">' + placeholder + '</option>';
    items.forEach(function(item) {
      var opt = document.createElement('option');
      opt.value = valueFn ? valueFn(item) : item;
      opt.textContent = labelFn ? labelFn(item) : item;
      if (item.size_code) opt.dataset.sizeCode = item.size_code;
      sel.appendChild(opt);
    });
    sel.disabled = false;
  }

  // ========================================
  // CASCADE: YEAR → MAKE → MODEL
  // ========================================
  function fetchYears() {
    resetSelect(yearSelect, 'Loading years…');
    resetSelect(makeSelect, 'Select Make');
    resetSelect(modelSelect, 'Select Model');
    updateYMMButton();

    return apiFetch({ action: 'years' }).then(function(years) {
      years.sort(function(a, b) { return b - a; });
      populateSelect(yearSelect, years, 'Select Year');
    }).catch(function() {
      resetSelect(yearSelect, 'Error loading years');
    });
  }

  function fetchMakes(year) {
    resetSelect(makeSelect, 'Loading makes…');
    resetSelect(modelSelect, 'Select Model');
    updateYMMButton();

    return apiFetch({ action: 'makes', year: year }).then(function(makes) {
      makes.sort();
      populateSelect(makeSelect, makes, 'Select Make');
    }).catch(function() {
      resetSelect(makeSelect, 'Error loading makes');
    });
  }

  function fetchModels(year, make) {
    resetSelect(modelSelect, 'Loading models…');
    updateYMMButton();

    // v2.3.0: DB is source of truth; static data is fallback only
    var staticData = window.HailProtector && window.HailProtector.vehicleData;
    var makeStatic = staticData && staticData[make] ? staticData[make] : null;

    return apiFetch({ action: 'models', year: year, make: make }).then(function(models) {
      // Use API size_code (DB-verified); fall back to static only if API has none
      models.forEach(function(m) {
        if (!m.size_code && makeStatic && makeStatic[m.model]) {
          m.size_code = makeStatic[m.model];
        }
      });
      models.sort(function(a, b) { return a.model.localeCompare(b.model); });
      populateSelect(
        modelSelect,
        models,
        'Select Model',
        function(m) { return m.model; },
        function(m) {
          var meta = SIZE_META[m.size_code];
          return m.model + (meta ? ' — ' + meta.series + ' ' + meta.tier : '');
        }
      );
    }).catch(function() {
      // API failed — fall back to static data if available
      if (makeStatic) {
        var staticModels = Object.keys(makeStatic).sort().map(function(name) {
          return { model: name, size_code: makeStatic[name] };
        });
        populateSelect(
          modelSelect,
          staticModels,
          'Select Model',
          function(m) { return m.model; },
          function(m) {
            var meta = SIZE_META[m.size_code];
            return m.model + (meta ? ' — ' + meta.series + ' ' + meta.tier : '');
          }
        );
      } else {
        resetSelect(modelSelect, 'Error loading models');
      }
    });
  }

  // ========================================
  // YMM BUTTON STATE
  // ========================================
  function updateYMMButton() {
    if (!ymmBtn) return;
    var ready = yearSelect.value && makeSelect.value && modelSelect.value;
    ymmBtn.disabled = !ready;
  }

  // ========================================
  // RESULT DISPLAY
  // ========================================
  function showResult(sizeCode, vehicleLabel) {
    var meta = SIZE_META[sizeCode];
    if (!meta) return;

    var seriesName = meta.series + ' ' + meta.tier;

    document.getElementById('result-series').textContent = seriesName;
    document.getElementById('result-size-code').textContent = '(' + sizeCode + ')';
    document.getElementById('result-range').textContent = meta.range;
    document.getElementById('result-vehicle').innerHTML =
      'Perfect for your <strong>' + vehicleLabel + '</strong>';
    document.getElementById('result-price').textContent = meta.price;
    document.getElementById('result-series-btn').textContent = meta.series;

    // CTA → PDP (localStorage will auto-restore on PDP)
    document.getElementById('result-link').href = PDP_URL;

    resultEl.style.display = 'block';
    resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function hideResult() {
    resultEl.style.display = 'none';
  }

  // ========================================
  // SAVE TO LOCALSTORAGE (shared with PDP)
  // ========================================
  function saveVehicle(year, make, model, sizeCode) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        year: String(year),
        make: make,
        model: model,
        sizeCode: sizeCode
      }));
    } catch (e) {}
  }

  // ========================================
  // EVENT HANDLERS — YMM CASCADE
  // ========================================
  yearSelect.addEventListener('change', function() {
    if (this.value) {
      fetchMakes(this.value);
    } else {
      resetSelect(makeSelect, 'Select Make');
      resetSelect(modelSelect, 'Select Model');
    }
    hideResult();
    updateYMMButton();
  });

  makeSelect.addEventListener('change', function() {
    if (this.value && yearSelect.value) {
      fetchModels(yearSelect.value, this.value);
    } else {
      resetSelect(modelSelect, 'Select Model');
    }
    hideResult();
    updateYMMButton();
  });

  modelSelect.addEventListener('change', function() {
    updateYMMButton();
  });

  // "Find My Size" button for YMM
  if (ymmBtn) {
    ymmBtn.addEventListener('click', function(e) {
      e.preventDefault();
      var year  = yearSelect.value;
      var make  = makeSelect.value;
      var model = modelSelect.value;
      var opt   = modelSelect.options[modelSelect.selectedIndex];
      var sizeCode = opt ? opt.dataset.sizeCode : null;

      if (!year || !make || !model || !sizeCode) return;

      saveVehicle(year, make, model, sizeCode);
      showResult(sizeCode, year + ' ' + make + ' ' + model);
    });
  }

  // ========================================
  // TAB SWITCHING
  // ========================================
  tabs.forEach(function(tab) {
    tab.addEventListener('click', function() {
      var target = tab.dataset.tab;
      tabs.forEach(function(t) { t.classList.remove('active'); });
      panels.forEach(function(p) { p.classList.remove('active'); });
      tab.classList.add('active');
      var panel = document.querySelector('[data-panel="' + target + '"]');
      if (panel) panel.classList.add('active');
      hideResult();
    });
  });

  // ========================================
  // POPULAR VEHICLE TAGS
  // ========================================
  popularTags.forEach(function(tag) {
    tag.addEventListener('click', function() {
      var year  = tag.dataset.year;
      var make  = tag.dataset.make;
      var model = tag.dataset.model;

      if (!year || !make || !model) return;

      // Set the dropdowns to match
      yearSelect.value = '';
      fetchYears().then(function() {
        yearSelect.value = year;
        return fetchMakes(year);
      }).then(function() {
        makeSelect.value = make;
        return fetchModels(year, make);
      }).then(function() {
        // Find the matching option
        for (var i = 0; i < modelSelect.options.length; i++) {
          if (modelSelect.options[i].value === model) {
            modelSelect.selectedIndex = i;
            var sizeCode = modelSelect.options[i].dataset.sizeCode;
            if (sizeCode) {
              saveVehicle(year, make, model, sizeCode);
              showResult(sizeCode, year + ' ' + make + ' ' + model);
            }
            break;
          }
        }
        updateYMMButton();
      });
    });
  });

  // ========================================
  // LENGTH CALCULATOR (preserved from v1)
  // ========================================
  typeBtns.forEach(function(btn) {
    btn.addEventListener('click', function() {
      typeBtns.forEach(function(b) { b.classList.remove('active'); });
      btn.classList.add('active');
    });
  });

  if (findByLengthBtn) {
    findByLengthBtn.addEventListener('click', function() {
      var length = parseInt(lengthInput.value);
      var activeBtn = document.querySelector('.sf-type-btn.active');
      var type = activeBtn ? activeBtn.dataset.type : 'car';

      if (!length || length < 100 || length > 300) {
        alert('Please enter a valid length between 100 and 300 inches.');
        return;
      }

      // Official sizing chart boundaries (corrected 2026-03-05)
      var size;
      if (type === 'car') {
        size = length <= 175 ? 'CAR1' : (length <= 195 ? 'CAR2' : 'CAR3');
      } else {
        size = length <= 197 ? 'SUV1' : (length <= 233 ? 'SUV2' : 'SUV3');
      }

      var label = length + '" ' + (type === 'car' ? 'Car' : 'Truck/SUV');
      showResult(size, label);
      // Don't save to localStorage for length-based — no YMM to restore
    });
  }

  // ========================================
  // RESET
  // ========================================
  if (resetBtn) {
    resetBtn.addEventListener('click', function() {
      hideResult();
      yearSelect.value = '';
      resetSelect(makeSelect, 'Select Make');
      resetSelect(modelSelect, 'Select Model');
      if (lengthInput) lengthInput.value = '';
      updateYMMButton();
    });
  }

  // ========================================
  // RESTORE FROM LOCALSTORAGE
  // ========================================
  function restoreSaved() {
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved && saved.year && saved.make && saved.model && saved.sizeCode) {
        return fetchYears().then(function() {
          yearSelect.value = saved.year;
          return fetchMakes(saved.year);
        }).then(function() {
          makeSelect.value = saved.make;
          return fetchModels(saved.year, saved.make);
        }).then(function() {
          modelSelect.value = saved.model;
          updateYMMButton();
          showResult(saved.sizeCode, saved.year + ' ' + saved.make + ' ' + saved.model);
        });
      }
    } catch (e) {}
    return null;
  }

  // ========================================
  // INIT
  // ========================================
  var restored = restoreSaved();
  if (!restored) {
    fetchYears();
  }
});
